// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Cache and polling tunables for the react-query layer (ADR-0008, Decision 6).
 *
 * These are deliberately configuration rather than literals scattered across
 * hooks, so freshness can be traded against bandwidth per environment without a
 * code review round. Every value keeps its code default: an unset, empty or
 * non-numeric var boots with today's behaviour rather than `NaN`.
 *
 * CRA substitutes `process.env.REACT_APP_*` at build time, so retuning a
 * deployed environment is a rebuild, not a restart.
 *
 * Environment variables (set in front/.env / CI):
 *   REACT_APP_QUERY_STALE_TIME_MS        – global staleTime default
 *   REACT_APP_QUERY_GC_TIME_MS           – global gcTime default
 *   REACT_APP_TWIN_STALE_TIME_MS         – digital twin graph staleTime
 *   REACT_APP_TWIN_POLL_INTERVAL_MS      – digital twin telemetry refetchInterval
 *   REACT_APP_WORKFLOW_POLL_INTERVAL_MS  – approvals / coverage poll interval
 *   REACT_APP_LIVE_POLL_INTERVAL_MS      – Live view measured-curve poll interval
 *   REACT_APP_LIVE_REFETCH_OVERLAP_MS    – how far back each Live poll re-reads
 *   REACT_APP_WEATHER_POLL_INTERVAL_MS   – Live view observed-weather poll interval
 *   REACT_APP_ALARMS_ACTIVE_POLL_INTERVAL_MS     – alarm/anomaly poll on a list route
 *   REACT_APP_ALARMS_BACKGROUND_POLL_INTERVAL_MS – alarm/anomaly poll elsewhere
 *   REACT_APP_ASK_HELIOS_IDLE_TIMEOUT_MS – Ask EnergyFluo stream idle timeout
 *   REACT_APP_WORK_ORDER_DIRECTORY_STALE_TIME_MS – work-order owner/provider lists staleTime
 *   REACT_APP_ME_STALE_TIME_MS           – /me (profile, roles, status) staleTime
 */

/**
 * Read a millisecond duration from the environment, falling back to
 * `fallback` when the var is unset, empty, or not a finite number.
 */
function msFromEnv(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Global `staleTime` default — how long any query's data is served as fresh. */
export const QUERY_STALE_TIME_MS = msFromEnv(process.env.REACT_APP_QUERY_STALE_TIME_MS, 30_000);

/** Global `gcTime` default — how long an unobserved query stays cached. */
export const QUERY_GC_TIME_MS = msFromEnv(process.env.REACT_APP_QUERY_GC_TIME_MS, 300_000);

/**
 * Digital twin graph `staleTime`. Deliberately long: the poll below drives
 * freshness while a twin page is open, so navigation back to a twin page should
 * paint from cache rather than refetch a multi-megabyte graph.
 */
export const TWIN_STALE_TIME_MS = msFromEnv(process.env.REACT_APP_TWIN_STALE_TIME_MS, 600_000);

/**
 * Digital twin telemetry `refetchInterval`. Per-observer, so polling starts when
 * a twin page mounts and stops when it unmounts while the cache survives.
 */
export const TWIN_POLL_INTERVAL_MS = msFromEnv(process.env.REACT_APP_TWIN_POLL_INTERVAL_MS, 60_000);

/** Poll interval for the workflow surfaces (approvals, coverage checklist/staging). */
export const WORKFLOW_POLL_INTERVAL_MS = msFromEnv(
  process.env.REACT_APP_WORKFLOW_POLL_INTERVAL_MS,
  10_000
);

/**
 * How often the Live view's measured curves advance, and the width of the
 * window each poll re-reads.
 *
 * The overlap exists because the bucket at the leading edge is always partial
 * when it is first read: re-asking for the last half hour every poll lets that
 * bucket converge, and absorbs any upstream backfill, for about 2% of a day's
 * payload.
 */
export const LIVE_POLL_INTERVAL_MS = msFromEnv(process.env.REACT_APP_LIVE_POLL_INTERVAL_MS, 60_000);

export const LIVE_REFETCH_OVERLAP_MS = msFromEnv(
  process.env.REACT_APP_LIVE_REFETCH_OVERLAP_MS,
  30 * 60_000
);

/**
 * How often the Live view re-reads observed weather, kept separate from the
 * measured-curve cadence above.
 *
 * The observations upstream is hourly (`PT1H`), so there is no new reading to
 * fetch more than once an hour, and its window must align to whole hours or the
 * relay 404s a sub-hour request. Polling it on the fast measured-curve interval
 * bought nothing but those 404s. Telemetry keeps `LIVE_POLL_INTERVAL_MS`; only
 * the weather curve advances on this slower clock.
 */
export const WEATHER_POLL_INTERVAL_MS = msFromEnv(
  process.env.REACT_APP_WEATHER_POLL_INTERVAL_MS,
  60 * 60_000
);

/**
 * Alarm / anomaly poll interval while the operator is ON a Home alarm list
 * route. Fast, because these lists are the queue an operator works from and a
 * state change made on a detail page (or by another operator) has to show up
 * without a manual refresh. See hooks/useAlarmsAutoRefresh.ts and ADR-0013.
 */
export const ALARMS_ACTIVE_POLL_INTERVAL_MS = msFromEnv(
  process.env.REACT_APP_ALARMS_ACTIVE_POLL_INTERVAL_MS,
  10_000
);

/**
 * Alarm / anomaly poll interval everywhere else in the app. The queries stay
 * observed from the shell rather than being torn down, so returning to the list
 * paints a recent cache entry instead of a skeleton — at a sixth of the request
 * volume, since nothing is on screen to be stale.
 */
export const ALARMS_BACKGROUND_POLL_INTERVAL_MS = msFromEnv(
  process.env.REACT_APP_ALARMS_BACKGROUND_POLL_INTERVAL_MS,
  60_000
);

/**
 * How long an Ask EnergyFluo stream may go without a single byte before the
 * client gives up. The BFF sends a heartbeat comment every 15s while tools run,
 * so 45s is three missed heartbeats: the connection is dead, not just slow.
 */
export const ASK_HELIOS_IDLE_TIMEOUT_MS = msFromEnv(
  process.env.REACT_APP_ASK_HELIOS_IDLE_TIMEOUT_MS,
  45_000
);

/**
 * `staleTime` of the work-order business-owner and provider directories. They
 * change only when someone edits them in the provider system, and upstream
 * caches them itself, so a create form reuses the last read for a while.
 */
export const WORK_ORDER_DIRECTORY_STALE_TIME_MS = msFromEnv(
  process.env.REACT_APP_WORK_ORDER_DIRECTORY_STALE_TIME_MS,
  5 * 60_000
);

/**
 * `staleTime` of `/me`, which refetches on window focus once stale (AB#35973).
 * Each `/me` is 4-5 uncached IAM/OMS calls, and the BFF's authz caches lag a
 * role change by up to 10 minutes anyway, so the 30 s global default would buy
 * load, not freshness.
 */
export const ME_STALE_TIME_MS = msFromEnv(process.env.REACT_APP_ME_STALE_TIME_MS, 5 * 60_000);
